<template>
  <div class="uk-grid-medium" uk-grid>
    <div class="uk-width-5-5">
      <div class="d-flex justify-content-between mb-3">
        <h3>{{ this.$t("general.all_contents") }} ({{ items.total }})</h3>
        <router-link v-if="$hasPermission('create_content')" :to="{ name: 'company.content.add' }"
          class="btn btn-default">
          <i class="uil-presentation-plus" /> {{ $t("general.add_content") }}
        </router-link>
        <button v-else class="btn btn-default" disabled style="cursor: not-allowed">
          <i class="uil-presentation-plus" /> {{ $t("general.add_content") }}
        </button>
      </div>

      <template v-if="errorMessages">
        <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close />
          <p style="color: red">
            <i class="uil-exclamation-triangle" /> {{ error }}
          </p>
        </div>
      </template>

      <div v-if="successMessage" class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close />
        <p style="color: green">
          <i class="uil-exclamation-triangle" /> {{ successMessage }}
        </p>
      </div>

      <div class="section-header mb-3">
        <div class="section-header-left">
          <input v-model="search" :placeholder="$t('general.search_in_contents')" class="mt-3 mb-3" type="text" />
        </div>
        <div class="section-header-right">
          <div class="btn-group bootstrap-select ml-3">
            <multiple-select-input :allowEmpty="false" :model.sync="filter.orderBy" :multiple="false" :required="false"
              :searchable="false" :showLabels="false" :values="filterTypes" @select="
                filter.orderBy = $event;
              sortOrder();
              " />
          </div>
        </div>
      </div>

      <div class="pb-4 uk-grid justify-content-between" uk-grid="">
        <div class="uk-width-3-4@m uk-first-column">
          <default-loading v-if="content_loading"></default-loading>
          <template v-else>
            <div class="uk-child-width-1-3@m uk-grid uk-first-column content-grid" uk-grid=""
              uk-scrollspy="target: > div; cls: uk-animation-slide-left-small; delay: 200">
              <div v-for="(item, index) in formatteditems" :key="index">
                <div class="card animate-this uk-inline-clip content-card">
                  <img :src="item.photo_link" alt="" style="height: 180px; width: 100%" />
                  <div class="content-card-content">
                    <div class="card-body text-center pb-3 content-card-heading">
                      <h6 class="mb-0 content-card-title">
                        {{ item.title }}
                      </h6>
                      <span class="d-block mt-1">{{ $t(item.key_name) }}</span>
                      <span class="d-block mt-1">{{ item.category_name }}</span>
                      <span class="d-block mt-1">{{ item.created_at }}</span>
                    </div>
                  </div>
                  <div class="card-footer py-3 border-top-0 content-card-footer">
                    <div class="content-card-actions d-flex flex-column">
                      <router-link v-if="$hasPermission('create_assignment')" :to="{
                        name: 'company.assignments.add',
                        params: {
                          id: item.id,
                          title: item.title,
                          name: item.name,
                        },
                      }" class="btn btn-info btn-sm mb-2">
                        <i class="uil-location-arrow"></i>
                        {{ $t("menu.company_dashboard.create_assignment") }}
                      </router-link>

                      <button v-else class="btn btn-info btn-sm mb-2" disabled :uk-tooltip="$t('general.no_permission')"
                        :style="{ cursor: 'not-allowed', opacity: 0.5 }">
                        <i class="uil-location-arrow"></i>
                        {{ $t("menu.company_dashboard.create_assignment") }}
                      </button>

                      <button class="btn btn-outline-secondary btn-sm mb-2" @click="openPreviewModal(item)">
                        <i class="uil-presentation-play" />
                        {{ $t("general.preview") }}
                      </button>
                      <router-link :to="$hasPermission('create_content')
                        ? { name: 'company.contents.edit', params: { id: item.id } }
                        : '#'" class="btn btn-secondary btn-sm mb-2" type="button"
                        v-bind="!$hasPermission('create_content') ? { 'uk-tooltip': $t('general.no_permission') } : {}"
                        :style="{
                          cursor: $hasPermission('create_content') ? 'pointer' : 'not-allowed',
                          pointerEvents: $hasPermission('create_content') ? 'auto' : 'none',
                          opacity: $hasPermission('create_content') ? 1 : 0.5,
                        }">
                        <i class="uil-cog"></i>{{ $t("general.edit") }}
                      </router-link>

                      <button v-if="$hasPermission('delete_content')" class="btn btn-secondary btn-sm mb-3"
                        @click.prevent="removeItem(item.id)" :style="{
                          cursor: 'pointer',
                          opacity: 1,
                        }">
                        <i class="uil-minus-circle"></i> {{ $t("general.delete") }}
                      </button>

                      <button v-else class="btn btn-secondary btn-sm mb-3" disabled
                        :uk-tooltip="$t('general.no_permission')" :style="{
                          cursor: 'not-allowed',
                          opacity: 0.5
                        }">
                        <i class="uil-minus-circle"></i> {{ $t("general.delete") }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12">
                <pagination v-if="pagination && pagination.hasOwnProperty('links')" :pagination="pagination"
                  @changePage="setPage($event)" />
              </div>
            </div>
          </template>
        </div>
        <div class="uk-width-expand">
          <div class="sidebar-filter card p-2">
            <div class="sidebar-filter-contents">
              <h4 style="text-transform: capitalize">
                <i class="il-filter" /> {{ this.$t("general.filter") }}
              </h4>
              <label>
                <input :data-id="'all'" checked class="uk-radio" data-type="all" name="filters" type="radio"
                  @change="setType" />
                <span style="margin-left: 6px">
                  {{ $t("general.all") }}
                </span>
              </label>

              <ul class="sidebar-filter-list uk-accordion" uk-accordion="multiple: true">
                <li class="uk-open">
                  <a class="uk-accordion-title" href="#">
                    {{ $t("general.by_content_type") }}
                  </a>
                  <div aria-hidden="false" class="uk-accordion-content" style="height: 32vh; overflow-y: scroll">
                    <div class="uk-form-controls">
                      <label v-for="(item, indis) in formattedfilters.file_type" v-if="item.contents_count > 0"
                        :key="`filters-0-${indis}`">
                        <input :data-id="item.id" class="uk-radio" data-type="file_type_id" name="filters" type="radio"
                          @change="setType" />
                        <span class="test">
                          {{ $t(item.key_name) }}
                          <span> ({{ item.contents_count }}) </span>
                        </span>
                      </label>
                      <label v-for="(item, i) in formattedfilters.content_type" v-if="item.contents_count > 0"
                        :key="`filters-1-${i}`">
                        <input :data-id="item.id" class="uk-radio" data-type="content_type_id" name="filters" type="radio"
                          @change="setType" />
                        <span class="test">
                          {{ $t('single.' + item.key_name) }}<span> ({{ item.contents_count }}) </span>
                        </span>
                      </label>
                    </div>
                  </div>
                </li>

                <li class="uk-open">
                  <a class="uk-accordion-title" href="#">
                    {{ $t("general.by_category_tag") }}
                  </a>
                  <div aria-hidden="false" class="uk-accordion-content">
                    <div class="form-group">
                      <label for="search-tag" style="font-size: 0.75rem"><i class="uil-search"></i>
                        {{ $t("general.tag_search") }}</label>
                      <input id="search-tag" v-model="searchTag" :placeholder="$t('general.type_a_tag')"
                        autocomplete="off" class="form-control mb-0" type="text" />
                    </div>
                    <div class="uk-form-controls" style="overflow-y: scroll; height: 10rem">
                      <label v-for="(item, index) in filteredTags" :key="`filteredTags-${index}`">
                        <input :data-id="item.name" class="uk-radio" data-type="hashtag" name="filters" type="radio"
                          @change="setType" />
                        <span class="test">{{ item.name }} </span>
                      </label>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div class="uk-sticky-placeholder" hidden="" style="height: 721px; margin: 0 0 20px" />
        </div>
      </div>
    </div>

    <!-- Content Preview Modal -->
    <content-preview-modal ref="contentPreviewModal" :selected-content="selectedContent"
      :preview-content="previewContentData" />
  </div>
</template>

<script>
import store from "@/core/services";
import moment from "moment";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import Pagination from "@/assets/components/Pagination";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import ContentPreviewModal from "@/view/components/modal/ContentPreviewModal";
import module, {
  BASE_URL,
  DELETE_ITEM,
  ERROR,
  FILTERS,
  FILTERS_URL,
  GET_FILTERS,
  GET_ITEMS,
  ITEMS,
  LOADING,
  MODULE_NAME,
} from "@/core/services/store/content.module";
import axios from "axios";
import { confirmDialogDelete } from "@/utils/confirmDialog";

export default {
  name: "Content",
  components: {
    DefaultLoading,
    MultipleSelectInput,
    Pagination,
    ContentPreviewModal,
  },
  data() {
    return {
      filterTypes: [
        {
          id: "title",
          name: this.$t("filter.Filter_by_name"),
        },
        {
          id: "category",
          name: this.$t("filter.Filter_by_category"),
        },
        {
          id: "created_at",
          name: this.$t("filter.Filter_by_date"),
        },
      ],
      page: 1,
      per_page: 2,
      successMessage: null,
      errorMessages: [],
      search: "",
      searchTag: "",
      filter: {
        orderBy: { id: "created_at", name: this.$t("filter.Filter_by_date") },
        sort: null,
        hashtag: null,
        file_type_id: null,
        content_type_id: null,
      },
      pagination: null,
      searchTagList: [],
      selectedContent: null,
      previewContentData: null,
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    filters: {
      get() {
        return store.getters[MODULE_NAME + "/" + FILTERS];
      },
      set(value) { },
    },
    content_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) { },
    },
    filteredTags() {
      const tags = this.searchTagList;
      const search = this.searchTag;
      if (search === "" || tags == null) return tags;

      const tag = tags.filter((tag) => {
        return tag.name.toLowerCase().includes(search.toLowerCase());
      });

      return tag;
    },
    items: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) { },
    },
    formattedfilters: {
      get() {
        let filters = this.filters;
        if (filters === null) return [];
        return filters;
      },
    },
    formatteditems: {
      get() {
        let results = [];
        let items = this.items.data;

        this.pagination = this.items;

        if (items === null || undefined == items) return [];
        for (let i = 0; i < items.length; i++) {
          let currentItem = items[i];
          if (currentItem) {
            results.push({
              title: currentItem.title,
              name: currentItem.name,
              category_name: currentItem.category_name,
              id: currentItem.id,
              key_name: currentItem.key_name,
              photo_link: currentItem.cover_photo_link,
              created_at: moment(currentItem.created_at).format(
                "DD/MM/YYYY HH:mm:ss"
              ),
              assignments: currentItem.assignments_count > 0 ? true : false,
            });
          }
        }
        return results;
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) { },
    },
  },
  methods: {
    filterResult() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          search: this.search,
          page: this.page,
          per_page: this.per_page,
          orderBy: this.filter.orderBy.id,
          sort: this.filter.sort,
          hashtag: this.filter.hashtag,
          file_type_id: this.filter.file_type_id,
          content_type_id: this.filter.content_type_id,
        },
      });
    },
    async removeItem(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        this.$store
          .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
            url: BASE_URL + "/" + id,
          })
          .then(() => {
            this.scrollToTop();
            if (!this.error) {
              this.successMessage = this.$t("general.successfully_deleted");
              this.filterResult();
            } else {
              this.errorMessages.push(this.error);
            }
          });
      }
    },

    sortOrder() {
      this.page = 1;
      if (
        this.filter.orderBy.id == "title" ||
        this.filter.orderBy.id == "category"
      ) {
        this.filter.sort = "asc";
      }
      this.filterResult();
    },
    setType(event) {
      let target = event.target;
      let id = target.getAttribute("data-id");
      let type = target.getAttribute("data-type");
      this.filter.file_type_id =
        this.filter.content_type_id =
        this.filter.hashtag =
        null;
      if (type == "hashtag") this.filter.hashtag = id;
      else if (type == "file_type_id") this.filter.file_type_id = id;
      else if (type == "content_type_id") this.filter.content_type_id = id;
      else if (type === "all") {
        this.getItems();
      }
      type !== "all" ? this.debouncedFilterType() : null;
    },
    setPage(page) {
      this.page = page;
      this.filterResult();
    },
    getContentFilters() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_FILTERS, {
        url: FILTERS_URL,
      });
    },
    async getFilteredTags() {
      const { data } = await axios.get(`/api/active-tags`);
      this.searchTagList = data;
    },
    getItems() {
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
      });
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
    async openPreviewModal(item) {
      try {
        const url = `/api/content/preview?content_id=${item.id}`;
        const response = await axios.get(url);

        let previewContent = null;

        if (response.data) {
          if (response.data.data) {
            previewContent = response.data.data;
          }
        }

        if (previewContent && this.$refs.contentPreviewModal) {
          this.selectedContent = item;
          this.previewContentData = previewContent;

          this.$refs.contentPreviewModal.showModal();
        } else {
          console.error("No preview content or modal ref not found!");
        }
      } catch (error) {
        console.error("Error fetching preview data:", error);
      }
    },
  },
  mounted() {
    this.getContentFilters();
    this.getFilteredTags();
    this.getItems();
    $(".selectpicker").selectpicker();
  },
  watch: {
    search: function (val) {
      this.debouncedFilterSearch();
    },
    successMessage: function (val) {
      this.debouncedResetValues();
    },
    errorMessages: function (val) {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.filterResult, 500);
    this.debouncedFilterType = _.debounce(this.filterResult, 300);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>
<style scoped>
@import "~vue-multiselect/dist/vue-multiselect.min.css";

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.content-grid>div {
  display: flex;
}

.content-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.content-card-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
}

.content-card-heading {
  min-height: 0;
}

.content-card-title {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.content-card-footer {
  margin-top: auto;
}

.content-card-actions {
  padding: 0 1rem;
}
</style>

<style>
.multiselect__tag {
  color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #3e416d;
}

.multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.multiselect__tag-icon:after {
  color: #fff;
}

.multiselect__tag-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.multiselect__input {
  border: unset !important;
}

.multiselect__tags .multiselect__input {
  border: unset !important;
}

.sidebar-filter {
  position: sticky;
  top: 80px;
  height: fit-content;
}
</style>
